/**
 * Site Theme Configurations Index
 *
 * Register all site themes here
 */

import { SiteType, SiteThemeConfig } from '@shared/providers/theme/types';
import { getTenantCustomizations } from '@customize/registry';
import { medixTheme } from './medix';

/**
 * Theme Registry - baseline themes plus every theme contributed by `customize/`.
 *
 * Built lazily: `customize/` imports back into `src/`, so reading the registry
 * at module-evaluation time can run while that import cycle is still in flight.
 */
let registry: Record<string, SiteThemeConfig> | null = null;

const themeRegistry = (): Record<string, SiteThemeConfig> => {
  if (!registry) {
    registry = getTenantCustomizations().reduce(
      (acc, tenant) => (tenant.theme ? { ...acc, [tenant.code]: tenant.theme } : acc),
      { medix: medixTheme } as Record<string, SiteThemeConfig>
    );
  }
  return registry;
};

/**
 * Default theme (used when site type is not found)
 */
export const defaultTheme = medixTheme;

/**
 * Get theme configuration for a site type
 */
export function getSiteTheme(siteType: SiteType): SiteThemeConfig {
  return themeRegistry()[siteType] || defaultTheme;
}

/**
 * Check if a site type has a registered theme
 */
export function hasSiteTheme(siteType: SiteType): boolean {
  return siteType in themeRegistry();
}

/**
 * Get all registered site types
 */
export function getRegisteredSiteTypes(): SiteType[] {
  return Object.keys(themeRegistry());
}

/**
 * Register a new site theme (for dynamic registration)
 */
export function registerSiteTheme(siteType: SiteType, config: SiteThemeConfig): void {
  themeRegistry()[siteType] = config;
}

export { medixTheme };
